// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
<v-dialog max-width='520' v-model="$store.state.showPlanetsVisibilityDialog">
  <v-card v-if="$store.state.showPlanetsVisibilityDialog" class="ds-min white--text">

    <div class="ds-min-head">
      <div class="ds-min-title">{{ $t('Planets Visibility') }}</div>
      <div class="ds-min-sub">{{ $t('Night from {0} to {1}', [startDate.format('D MMMM'), endDate.format('D MMMM')]) }}</div>
    </div>

    <div class="ds-min-list">
      <div v-for="obj in rows" :key="obj.name" class="ds-row" :class="'ds-row-' + obj.status" @click="selectObject(obj)">
        <span class="ds-row-icon" v-html="icons[obj.name]"></span>
        <div class="ds-row-text">
          <div class="ds-row-name">{{ obj.localName }}</div>
          <div class="ds-row-line">
            <template v-if="obj.status === 'sun'">{{ $t('Rise') }} {{ obj.rise }} · {{ $t('Set') }} {{ obj.set }}</template>
            <template v-else-if="obj.status === 'none'">{{ $t('Not observable tonight') }}</template>
            <template v-else>{{ obj.visText }} · {{ $t('in the {0}', [obj.bestDir]) }}</template>
          </div>
        </div>
        <div v-if="obj.status !== 'none' && obj.status !== 'sun'" class="ds-row-best">
          <div class="ds-row-time">{{ obj.bestTime }}</div>
          <div class="ds-row-lbl">{{ $t('Best time') }}</div>
        </div>
        <div v-else-if="obj.status === 'sun'" class="ds-row-best">
          <div class="ds-row-time ds-row-sun">☀</div>
        </div>
      </div>
    </div>

    <div class="ds-min-actions">
      <v-btn text class="ds-min-close" @click.native="$store.state.showPlanetsVisibilityDialog = false">{{ $t('Close') }}</v-btn>
    </div>
  </v-card>
</v-dialog>
</template>

<script>

import Moment from 'moment'

const ICONS = {
  Sun: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="11" fill="#ffd24a"/></svg>',
  Moon: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="11" fill="#d5dcea"/><circle cx="15" cy="16" r="2" fill="#aeb8cc"/><circle cx="24" cy="22" r="2.6" fill="#aeb8cc"/></svg>',
  Mercury: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="10" fill="#a89f94"/></svg>',
  Venus: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="11" fill="#eac48a"/></svg>',
  Mars: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="11" fill="#d4593a"/></svg>',
  Jupiter: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="11" fill="#d7ad78"/><path d="M9 17h22M9 23h22" stroke="#b58553" stroke-width="1.4"/></svg>',
  Saturn: '<svg viewBox="0 0 40 40"><ellipse cx="20" cy="20" rx="16" ry="4.5" fill="none" stroke="#d9c48f" stroke-width="2" transform="rotate(-18 20 20)"/><circle cx="20" cy="20" r="9" fill="#dcc18a"/></svg>'
}

export default {
  data: function () {
    return {
      icons: ICONS,
      planetKeys: [
        { stel: 'NAME Sun', key: 'Sun' },
        { stel: 'NAME Moon', key: 'Moon' },
        { stel: 'NAME Mercury', key: 'Mercury' },
        { stel: 'NAME Venus', key: 'Venus' },
        { stel: 'NAME Mars', key: 'Mars' },
        { stel: 'NAME Jupiter', key: 'Jupiter' },
        { stel: 'NAME Saturn', key: 'Saturn' }
      ]
    }
  },
  methods: {
    selectObject: function (obj) {
      const stelObj = this.$stel.getObj('NAME ' + obj.name)
      if (stelObj) {
        this.$stel.core.selection = stelObj
        this.$stel.pointAndLock(stelObj)
        this.$store.state.showPlanetsVisibilityDialog = false
      }
    },
    fmtTime: function (mjd) {
      if (mjd === undefined || mjd === null || isNaN(mjd)) return '—'
      const d = new Date()
      d.setMJD(mjd)
      return Moment.utc(d).local().format('HH:mm')
    },
    altitudeAt: function (obj, obs) {
      const azalt = this.$stel.convertFrame(obs, 'ICRF', 'OBSERVED', obj.getInfo('radec', obs))
      return this.$stel.anpm(this.$stel.c2s(azalt)[1]) * 180.0 / Math.PI
    },
    azimuthAt: function (obj, obs) {
      const azalt = this.$stel.convertFrame(obs, 'ICRF', 'OBSERVED', obj.getInfo('radec', obs))
      const az = this.$stel.anp(this.$stel.c2s(azalt)[0]) * 180.0 / Math.PI
      return (az + 180.0) % 360.0
    },
    directionKey: function (az) {
      const keys = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest']
      return keys[Math.round(az / 45) % 8]
    }
  },
  computed: {
    rows: function () {
      const result = []
      const sun = this.$stel.getObj('NAME Sun')
      const startMjd = this.startDate.toDate().getMJD()
      const obs = this.$stel.core.observer.clone()

      // Высота Солнца по ночи, шаг 20 мин (тёмное небо = Солнце ниже -6°)
      const steps = 24 * 3
      const stepMjd = 20 / 1440
      const sunAlt = []
      for (let i = 0; i <= steps; i++) {
        obs.utc = startMjd + i * stepMjd
        sunAlt.push(this.altitudeAt(sun, obs))
      }

      for (const pk of this.planetKeys) {
        const obj = this.$stel.getObj(pk.stel)
        if (!obj) continue
        const isSun = pk.key === 'Sun'
        const vis = obj.computeVisibility()[0]
        const riseMjd = vis ? vis.rise : undefined
        const setMjd = vis ? vis.set : undefined

        // Лучшее время = максимальная высота в тёмные часы
        let maxAltDark = -90
        let bestMjd = null
        let bestAz = 0
        if (!isSun) {
          for (let i = 0; i <= steps; i++) {
            if (sunAlt[i] >= -6) continue
            obs.utc = startMjd + i * stepMjd
            const alt = this.altitudeAt(obj, obs)
            if (alt > maxAltDark) { maxAltDark = alt; bestMjd = obs.utc; bestAz = this.azimuthAt(obj, obs) }
          }
        }
        const observable = !isSun && maxAltDark > 10

        let visText = ''
        if (observable) {
          const allNight = riseMjd === undefined || riseMjd === null || isNaN(riseMjd)
          visText = allNight ? this.$t('Visible all night') : this.$t('Visible from {0} to {1}', [this.fmtTime(riseMjd), this.fmtTime(setMjd)])
        }

        result.push({
          name: pk.key,
          localName: this.$t(pk.key),
          status: isSun ? 'sun' : (observable ? 'ok' : 'none'),
          rise: this.fmtTime(riseMjd),
          set: this.fmtTime(setMjd),
          bestTime: bestMjd === null ? '—' : this.fmtTime(bestMjd),
          bestDir: this.$t(this.directionKey(bestAz)),
          visText: visText
        })
      }
      obs.destroy()
      // Солнце → видимые → невидимые
      const rank = { sun: 0, ok: 1, none: 2 }
      return result.sort((a, b) => rank[a.status] - rank[b.status])
    },
    startDate: function () {
      const sun = this.$stel.getObj('NAME Sun')
      let u = this.$store.state.stel.observer.utc
      if (u < sun.rise) {
        u = u - 1
      }
      let d = new Date()
      d.setMJD(u)
      d = new Moment(d)
      d.local()
      d.hours(12); d.minutes(0); d.seconds(0)
      return d
    },
    endDate: function () {
      const d = new Moment(this.startDate)
      d.add(1, 'd')
      return d
    }
  }
}
</script>

<style scoped>
.ds-min {
  background: #111a2e !important;
  border-radius: 18px !important;
  overflow: hidden;
}
.ds-min-head { padding: 20px 22px 8px; }
.ds-min-title { font-size: 20px; font-weight: 400; color: #eaf1ff; letter-spacing: 0.3px; }
.ds-min-sub { font-size: 12px; color: #7f95bd; margin-top: 2px; }

.ds-min-list { padding: 4px 10px; }
.ds-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.ds-row:hover { background: rgba(110, 168, 255, 0.1); }
.ds-row + .ds-row { border-top: 1px solid rgba(255, 255, 255, 0.05); }
.ds-row-none { opacity: 0.45; }

.ds-row-icon { width: 30px; height: 30px; flex: 0 0 30px; }
.ds-row-icon >>> svg { width: 30px; height: 30px; display: block; }

.ds-row-text { flex: 1 1 auto; min-width: 0; }
.ds-row-name { font-size: 15px; font-weight: 500; color: #eaf1ff; }
.ds-row-line {
  font-size: 12px;
  color: #a9bce0;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.ds-row-none .ds-row-line { color: #7484a2; }

.ds-row-best { flex: 0 0 auto; text-align: right; }
.ds-row-time { font-size: 18px; font-weight: 300; color: #6ea8ff; font-variant-numeric: tabular-nums; line-height: 1.1; }
.ds-row-sun { color: #ffd24a; font-size: 16px; }
.ds-row-lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #6b80a6; margin-top: 2px; }

.ds-min-actions { display: flex; justify-content: flex-end; padding: 4px 14px 10px; }
.ds-min-close { color: #b98bff !important; }
</style>
